import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Dna } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { getLineage } from "@/lib/site";

const LINES = [
  {
    id: "cappuccino",
    n: "01",
    name: "Cappuccino",
    goal: "Mål: Visual Phantom Frappuccino",
    text: "Vores vision er at producere den ultimative Visual Phantom Frappuccino. Vi arbejder målrettet med at kombinere dybe Phantom-kontraster med Cappuccino og Lillywhite.",
  },
  {
    id: "axanthic",
    n: "02",
    name: "Axanthic",
    goal: "Mål: Visual Axanthic Lillywhite",
    text: "Formålet er at perfektionere den monokrome kronegekko. Vi stræber efter den rene Visual Axanthic Lillywhite, hvor de varme farver er helt væk.",
  },
  {
    id: "tri-color",
    n: "03",
    name: "Tri-color",
    goal: "Mål: High-Expression Tricolors",
    text: "Vi avler for maksimal farveintensitet. Vores mål er High-Expression Tricolors med kulsort base, lysende orange ryg og hvide sidepletter.",
  },
  {
    id: "confetti",
    n: "04",
    name: "Confetti Dalmatian",
    goal: "Mål: Røde Confetti-prikker",
    text: "Vores mål er at lave meget pæne Dalmatian-gekkoer med specielt fokus på de eftertragtede røde Confetti-prikker.",
  },
];

const GEN_ORDER = ["P1", "F1", "F2", "F3"];
const GEN_LABEL = { P1: "Avlsdyr", F1: "Første generation", F2: "Anden generation", F3: "Tredje generation" };

function AnimalCard({ a, big = false }) {
  return (
    <div
      data-aid={a.id}
      data-testid={`animal-${a.id}`}
      className={`relative border border-white/10 bg-moss ${big ? "w-full" : "w-36 sm:w-44"}`}
    >
      {a.image ? (
        <img
          src={a.image}
          alt={a.name}
          className={`w-full object-cover ${big ? "aspect-[4/3]" : "aspect-square"}`}
        />
      ) : (
        <div
          className={`flex w-full items-center justify-center bg-ink ${big ? "aspect-[4/3]" : "aspect-square"}`}
        >
          <Dna className="h-8 w-8 text-forest/50" strokeWidth={1.5} />
        </div>
      )}
      <div className={`${big ? "p-5" : "p-3"}`}>
        <span className="absolute right-2 top-2 rounded-full border border-forest/50 bg-ink/80 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-pale backdrop-blur-sm">
          {a.generation}
        </span>
        <p className={`font-serif italic tracking-tight text-cream ${big ? "text-2xl" : "text-base"}`}>
          {a.name}
        </p>
        {a.morph && (
          <p className={`mt-1 font-mono uppercase tracking-[0.15em] text-sage ${big ? "text-xs" : "text-[9px]"}`}>
            {a.morph}
          </p>
        )}
      </div>
    </div>
  );
}

function FamilyTree({ animals, lineId }) {
  const containerRef = useRef(null);
  const [paths, setPaths] = useState([]);

  const measure = () => {
    const container = containerRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const byId = Object.fromEntries(animals.map((a) => [a.id, a]));
    const next = [];
    animals.forEach((a) => {
      (a.parents || []).forEach((pid) => {
        if (!byId[pid]) return;
        const childEl = container.querySelector(`[data-aid="${a.id}"]`);
        const parentEl = container.querySelector(`[data-aid="${pid}"]`);
        if (!childEl || !parentEl) return;
        const c = childEl.getBoundingClientRect();
        const p = parentEl.getBoundingClientRect();
        const x1 = p.left + p.width / 2 - cRect.left;
        const y1 = p.bottom - cRect.top;
        const x2 = c.left + c.width / 2 - cRect.left;
        const y2 = c.top - cRect.top;
        const midY = y1 + (y2 - y1) / 2;
        next.push(`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`);
      });
    });
    setPaths(next);
  };

  useLayoutEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    const t1 = setTimeout(measure, 400);
    const t2 = setTimeout(measure, 1500);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [animals]);

  const rows = GEN_ORDER.map((g) => ({
    gen: g,
    animals: animals.filter((a) => a.generation === g),
  })).filter((r) => r.animals.length > 0);

  return (
    <div ref={containerRef} className="relative" data-testid={`family-tree-${lineId}`}>
      <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
        {paths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="rgba(163,167,156,0.35)" strokeWidth="1.5" />
        ))}
      </svg>
      <div className="space-y-10">
        {rows.map((row) => (
          <div key={row.gen}>
            <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-forest">
              {row.gen} · {GEN_LABEL[row.gen]}
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {row.animals.map((a) => (
                <AnimalCard key={a.id} a={a} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LinjeavlPage() {
  const navigate = useNavigate();
  const [animals, setAnimals] = useState([]);

  useEffect(() => {
    getLineage().then(setAnimals).catch(() => {});
  }, []);

  return (
    <div data-testid="linjeavl-page" className="px-6 pb-24 pt-32 lg:px-16 lg:pb-36">
      <Reveal>
        <SectionTag index="06" label="Linjeavl" />
        <h1 className="mt-6 max-w-3xl font-serif text-4xl tracking-tight text-cream sm:text-5xl lg:text-6xl">
          GEKKOFUEL <span className="italic text-pale">Linjeavl</span>
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Fire linjer. Dokumenteret generation for generation. Her kan I følge hvert eneste
          avlsdyr (P1) og deres afkom (F1, F2, F3) — med billeder, morfer og hele
          stamtræet samlet ét sted.
        </p>
      </Reveal>

      <div className="mt-20 space-y-24">
        {LINES.map((l, i) => {
          const lineAnimals = animals.filter((a) => a.line === l.id);
          const breeders = lineAnimals.filter((a) => a.generation === "P1");
          const offspring = lineAnimals.filter((a) => a.generation !== "P1");
          return (
            <Reveal key={l.id} delay={i * 0.05}>
              <section data-testid={`line-section-${l.id}`} className="border-t border-white/10 pt-14">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                  <span className="font-mono text-xs tracking-[0.25em] text-forest lg:col-span-2">
                    {l.n}
                  </span>
                  <div className="lg:col-span-4">
                    <h2 className="font-serif text-3xl italic tracking-tight text-cream">
                      {l.name}
                    </h2>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pale/70">
                      {l.goal}
                    </p>
                  </div>
                  <p className="max-w-lg text-base leading-relaxed text-sage lg:col-span-5">
                    {l.text}
                  </p>
                </div>

                {breeders.length > 0 && (
                  <div className="mt-12">
                    <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-pale">
                      Nuværende avlsdyr
                    </p>
                    <div
                      data-testid={`breeders-${l.id}`}
                      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    >
                      {breeders.map((a) => (
                        <AnimalCard key={a.id} a={a} big />
                      ))}
                    </div>
                  </div>
                )}

                {lineAnimals.length > 0 ? (
                  <div className="mt-14">
                    <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-pale">
                      Stamtræ
                    </p>
                    <FamilyTree animals={lineAnimals} lineId={l.id} />
                  </div>
                ) : (
                  <p className="mt-10 border border-dashed border-white/15 p-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-sage/70">
                    Stamtræet er under opbygning — første dyr annonceres snart
                  </p>
                )}

                {offspring.length > 0 && (
                  <div className="mt-14">
                    <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-pale">
                      Afkom fra linjen
                    </p>
                    <div
                      data-testid={`offspring-${l.id}`}
                      className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
                    >
                      {offspring.map((a) => (
                        <AnimalCard key={a.id} a={a} />
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </Reveal>
          );
        })}
      </div>

      <Reveal>
        <div className="mt-16 flex flex-wrap items-center justify-between gap-6 border border-forest/40 bg-forest/10 p-8">
          <p className="max-w-xl font-serif text-2xl italic leading-snug text-cream">
            Se hvilke kronegekkoer vi har på lager lige nu.
          </p>
          <button
            data-testid="linjeavl-morphs-button"
            onClick={() => {
              navigate("/");
              setTimeout(() => window.__lenis?.scrollTo("#morphs", { offset: -64 }), 450);
            }}
            className="group flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
          >
            Udforsk morferne
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>
    </div>
  );
}
